import { externalNetworkClient } from '@terra/shared-rest-clients-external-network';
import type {
  CloudInventorySurfaceAccountBodyDto,
  CloudInventorySurfaceProviderBodyDto,
  ConfirmDetectWebAppsBodyDto,
  DnsZoneSurfaceAttachBodyDto,
  DnsZoneSurfaceDetachBodyDto,
  DnsZoneSurfaceProviderBodyDto,
  ExternalNetworkContract,
  UpdateCloudAccountPolicyBodyDto,
  UpdateDnsZonePolicyBodyDto,
} from '@terra/shared-contracts-external-network';
import type { WebAppDraftStrategyType } from '@terra/shared-schemas-orchestrator';
import { tenantAwareApiWrapper, withOrchestratorClient } from './api-core';

export const externalNetworkInventoryApi = {
  listCloudInventoryAttachments: (surfaceId: string) =>
    tenantAwareApiWrapper<
      typeof ExternalNetworkContract.listCloudInventoryAttachments
    >({
      endpointFn: (headers) =>
        externalNetworkClient.listCloudInventoryAttachments({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  attachCloudInventory: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: CloudInventorySurfaceAccountBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.attachCloudInventory>({
      endpointFn: (headers) =>
        externalNetworkClient.attachCloudInventory({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  detachCloudInventory: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: CloudInventorySurfaceAccountBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.detachCloudInventory>({
      endpointFn: (headers) =>
        externalNetworkClient.detachCloudInventory({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  syncCloudInventory: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: CloudInventorySurfaceProviderBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.syncCloudInventory>({
      endpointFn: (headers) =>
        externalNetworkClient.syncCloudInventory({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  updateCloudAccountPolicy: ({
    surfaceId,
    accountId,
    body,
  }: {
    surfaceId: string;
    accountId: string;
    body: UpdateCloudAccountPolicyBodyDto;
  }) =>
    tenantAwareApiWrapper<
      typeof ExternalNetworkContract.updateCloudAccountPolicy
    >({
      endpointFn: (headers) =>
        externalNetworkClient.updateCloudAccountPolicy({
          params: { surfaceId, accountId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  listDnsZoneAttachments: (surfaceId: string) =>
    tenantAwareApiWrapper<
      typeof ExternalNetworkContract.listDnsZoneAttachments
    >({
      endpointFn: (headers) =>
        externalNetworkClient.listDnsZoneAttachments({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  attachDnsZone: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: DnsZoneSurfaceAttachBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.attachDnsZone>({
      endpointFn: (headers) =>
        externalNetworkClient.attachDnsZone({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  detachDnsZone: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: DnsZoneSurfaceDetachBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.detachDnsZone>({
      endpointFn: (headers) =>
        externalNetworkClient.detachDnsZone({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  syncDnsZones: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: DnsZoneSurfaceProviderBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.syncDnsZones>({
      endpointFn: (headers) =>
        externalNetworkClient.syncDnsZones({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  verifyDnsZoneDelegation: ({
    surfaceId,
    zoneId,
  }: {
    surfaceId: string;
    zoneId: string;
  }) =>
    tenantAwareApiWrapper<
      typeof ExternalNetworkContract.verifyDnsZoneDelegation
    >({
      endpointFn: (headers) =>
        externalNetworkClient.verifyDnsZoneDelegation({
          params: { surfaceId, zoneId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  updateDnsZonePolicy: ({
    surfaceId,
    zoneId,
    body,
  }: {
    surfaceId: string;
    zoneId: string;
    body: UpdateDnsZonePolicyBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.updateDnsZonePolicy>({
      endpointFn: (headers) =>
        externalNetworkClient.updateDnsZonePolicy({
          params: { surfaceId, zoneId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  listWebAppClusters: (surfaceId: string) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listWebAppClusters>({
      endpointFn: (headers) =>
        externalNetworkClient.listWebAppClusters({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  previewWebAppDrafts: ({
    surfaceId,
    strategy,
  }: {
    surfaceId: string;
    strategy?: WebAppDraftStrategyType;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.previewWebAppDrafts>({
      endpointFn: (headers) =>
        externalNetworkClient.previewWebAppDrafts({
          params: { surfaceId },
          query: strategy ? { strategy } : undefined,
          ...withOrchestratorClient(headers),
        }),
    }),

  confirmDetectWebApps: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: ConfirmDetectWebAppsBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.confirmDetectWebApps>({
      endpointFn: (headers) =>
        externalNetworkClient.confirmDetectWebApps({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),
};
